import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  Channel,
  channelApiClient,
  ChannelForm,
  ChannelUpdateErrorResponse,
} from '@src/api/shared/community/channelApiClient';
import {
  channelMemberApiClient,
  ChannelMemberShowResponse,
} from '@src/api/shared/community/channelMemberApiClient';
import defaultUserIcon from '@src/assets/images/avatar/default.png';
import SvgIconLock from '@src/assets/images/common/icon/lock/SvgIconLock';
import CreateEditChannelModal from '@src/components/CreateEditChannelModal.tsx';
import { useCompanyAuth } from '@src/context/auth/useCompanyAuth';
import { useTenantAuth } from '@src/context/auth/useTenantAuth';
import { MobileSidebarContext } from '@src/context/MobileSidebarContext';
import { ChannelVisibility } from '@src/features/shared/community/enums/channelVisibility';
import { useToast } from '@src/features/toast/context/toastContext';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import { CommunityMembersDetailProps } from '@src/pages/shared/community/CommunityMembersDetail';
import { SHARED_URL } from '@src/routes/shared';
import { COMMUNITY_MEMBERS_DETAIL_MODE } from '@src/utils/channelMember';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import CommunityChannelHeader from './components/CommunityChannelHeader';

const CommunityMemberShow: React.FC<CommunityMembersDetailProps> = ({ mode }) => {
  const { code, id } = useParams<{ code: string; id: string }>();
  const [channel, setChannel] = useState<Channel>();
  const [openChannelSettingModal, setOpenChannelSettingModal] = useState<boolean>(false);
  const [channelMember, setChannelMember] = useState<ChannelMemberShowResponse>();
  const [openSettingMenu, setOpenSettingMenu] = useState<boolean>(false);
  const { toggle: toggleMobileSidebar } = useContext(MobileSidebarContext);
  const tenantAuth = useTenantAuth();
  const companyAuth = useCompanyAuth();
  const menuRef = useRef<HTMLDivElement>(null);
  const isLoggedIn = !!(tenantAuth.profile?.isLogin || companyAuth.profile?.isLogin);
  const isTenantAdmin = !!tenantAuth.profile?.user?.isAdmin;
  const navigate = useNavigate();
  const [errors, setErrors] = useState<ChannelUpdateErrorResponse | null>(null);
  const toast = useToast();

  const getApiClient = useCallback(
    async (memberId: number) => {
      if (!code || !channel) {
        return;
      }
      if (mode === COMMUNITY_MEMBERS_DETAIL_MODE.TENANT) {
        return channelMemberApiClient.publicChannelTenantUserShow(code, memberId);
      }
      if (mode === COMMUNITY_MEMBERS_DETAIL_MODE.COMPANY) {
        return channelMemberApiClient.publicChannelCompanyUserShow(code, memberId);
      }
      return;
    },
    [code, channel, mode],
  );

  const fetchChannelMember = useCallback(async () => {
    if (!code || !channel) {
      return;
    }
    const res = await getApiClient(Number(id));
    if (!res) {
      return;
    }
    handleApiResponse(res, (data) => {
      setChannelMember(data);
    });
  }, [code, channel, id, getApiClient]);

  const loadChannel = useCallback(async () => {
    if (!code) {
      return;
    }
    const res = await channelApiClient.show(code, isTenantAdmin ? { force_show: true } : undefined);
    handleApiResponse(res, (data) => {
      setChannel(data.channel);
    });
  }, [code, isTenantAdmin]);

  const handleSettingMenuClick = () => {
    setOpenSettingMenu(true);
  };

  const handleChannelSettingOpenButtonClick = () => {
    setOpenSettingMenu(false);
    setOpenChannelSettingModal(true);
  };

  const handleChannelSettingModalClose = () => {
    setErrors(null);
    setOpenChannelSettingModal(false);
  };

  const handleLinkClick = (_e: React.MouseEvent<HTMLElement>, to: string) => {
    navigate(to);
  };

  useEffect(() => {
    loadChannel();
  }, [code, loadChannel]);

  useEffect(() => {
    fetchChannelMember();
  }, [fetchChannelMember]);

  const onSave = async (formData: ChannelForm) => {
    if (!code) {
      return;
    }
    setErrors(null);
    const res = await channelApiClient.update(code, { channel: formData });
    handleApiResponse(
      res,
      async () => {
        setOpenChannelSettingModal(false);
        await loadChannel();
        toast(TOAST_MESSAGE.UPDATE);
      },
      (errors) => {
        setErrors(errors);
      },
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenSettingMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <CommunityChannelHeader
        channel={channel || null}
        openSettingMenu={openSettingMenu}
        menuRef={menuRef}
        isTenantAdmin={isTenantAdmin}
        isLoggedIn={isLoggedIn}
        code={code}
        toggleMobileSidebar={toggleMobileSidebar}
        handleSettingMenuClick={handleSettingMenuClick}
        handleChannelSettingOpenButtonClick={handleChannelSettingOpenButtonClick}
        handleLinkClick={handleLinkClick}
        navigate={navigate}
        showDisplayNameBanner={false}
        isSticky={false}
        containerClassName='hidden lg:flex'
        innerContainerClassName='h-[60px]'
      />
      {channelMember && (
        <div className='px-[120px] pb-[120px] pt-[60px] lg:px-[60px] md:px-4 md:py-8'>
          <div className='flex flex-col md:px-4'>
            <div className='flex items-center gap-10 md:gap-6'>
              <img
                width={100}
                height={100}
                className='size-[100px] rounded-full object-cover'
                src={channelMember.avatar_url || defaultUserIcon}
                alt=''
              />
              <h1 className='text-xl font-semibold leading-[1.6]'>{channelMember.display_name}</h1>
            </div>
            <dl className='mt-10 flex flex-col md:mt-7 md:gap-6'>
              <div className='flex py-[18px] md:flex-col md:gap-[7px] md:py-1 [&:not(:first-child)]:border-gray-200 [&:not(:last-child)]:border-b'>
                <dt className='w-[180px] shrink-0 text-sm font-semibold'>企業名</dt>
                <dd className='px-8 md:px-0'>{channelMember.company_name}</dd>
              </div>
              <div className='flex py-[18px] md:flex-col md:gap-[7px] md:py-1 [&:not(:first-child)]:border-gray-200 [&:not(:last-child)]:border-b'>
                <dt className='w-[180px] shrink-0 text-sm font-semibold'>所属</dt>
                <dd className='px-8 md:px-0'>{channelMember.affiliation}</dd>
              </div>
              <div className='flex py-[18px] md:flex-col md:gap-[7px] md:py-1 [&:not(:first-child)]:border-gray-200 [&:not(:last-child)]:border-b'>
                <dt className='w-[180px] shrink-0 text-sm font-semibold'>役職</dt>
                <dd className='px-8 md:px-0'>{channelMember.position}</dd>
              </div>
              <div className='flex py-[18px] md:flex-col md:gap-[7px] md:py-1 [&:not(:first-child)]:border-gray-200 [&:not(:last-child)]:border-b'>
                <dt className='w-[180px] shrink-0 text-sm font-semibold'>業種</dt>
                <dd className='px-8 md:px-0'>{channelMember.industry_text}</dd>
              </div>
              <div className='flex py-[18px] md:flex-col md:gap-[7px] md:py-1 [&:not(:first-child)]:border-gray-200 [&:not(:last-child)]:border-b'>
                <dt className='w-[180px] shrink-0 text-sm font-semibold'>自己紹介</dt>
                <dd className='whitespace-pre-line px-8 md:px-0'>{channelMember.introduction}</dd>
              </div>
            </dl>
          </div>
          <div className='mt-[60px] md:mt-6 md:px-4'>
            <h2 className='text-xl font-semibold'>参加チャンネル</h2>
            <div className='mt-6 flex w-full flex-col md:mt-2'>
              <div className='flex w-full items-center border border-gray-200 bg-gray-50 py-3 text-xs md:hidden'>
                <div className='w-[43.75%] px-4 font-semibold'>チャンネル名</div>
                <div className='min-w-[80px] grow px-4 font-semibold'>参加者数</div>
                <div className='w-[43.75%] px-4 font-semibold'>チャンネル概要</div>
              </div>
              {channelMember.channel_section &&
                channelMember.channel_section.map((channel) => (
                  <div
                    key={channel.code}
                    className='flex w-full items-center py-5 text-sm md:max-h-full md:flex-col md:items-start md:!bg-white md:py-4 md:text-[16px] md:[&:not(:last-child)]:border-b md:[&:not(:last-child)]:border-gray-200 [&:nth-child(2n+1)]:bg-[#fcfcfd]'
                  >
                    <div className='flex w-[43.75%] items-center gap-2 px-4 md:w-full md:gap-[18px] md:px-0 md:py-1'>
                      <a
                        href={generatePath(SHARED_URL.COMMUNITY_CHANNEL, {
                          code: channel.code,
                        })}
                        className='line-clamp-2 break-all font-semibold text-primary-500 hover:underline'
                      >
                        {channel.name}
                      </a>
                      {(channel.visibility === ChannelVisibility.LIMITED ||
                        channel.visibility === ChannelVisibility.PRIVATE) && (
                        <div className='shrink-0'>
                          <SvgIconLock color='#BBBBBB' />
                        </div>
                      )}
                    </div>
                    <div className='min-w-[80px] grow px-4 md:flex md:w-full md:items-center md:gap-4 md:px-0 md:py-2'>
                      <div className='hidden text-sm font-semibold md:block'>参加者数</div>
                      <div className='font-medium'>{channel.members_count} 人</div>
                    </div>
                    <div className='line-clamp-3 w-[43.75%] break-all px-4 leading-[1.6] md:w-full md:px-0'>
                      {channel.description}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
      {channel && (
        <CreateEditChannelModal
          isOpen={openChannelSettingModal}
          channel={channel}
          onSave={onSave}
          onClose={handleChannelSettingModalClose}
          errors={errors || undefined}
        />
      )}
    </>
  );
};

export default CommunityMemberShow;
